import { Box, useToken } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { MdDeleteOutline, MdEdit } from 'react-icons/md';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftDialogWithExitConfirmation from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import type { ExitConfirmationDraftAction } from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import type { Column } from '@/components/Organisms/DataTable/DataTable.types';
import DataTable from '@/components/Organisms/DataTable';
import type {
	ColumnData,
	onFilterApplyProps,
} from '@/components/Organisms/DataTable/DataTable.types';
import BenefitVerificationForm from '@/components/Templates/BenefitVerification/BenefitVerificationForm';
import { BulkConsignmentOrderForm } from '@/components/Templates/ConsignmentOrders/BulkConsignmentOrderForm';
import PatientForm, {
	type PatientFormData,
} from '@/components/Templates/ManagePatients/PatientForm';
import { BulkOrderRequestForm } from '@/components/Templates/Requests/BulkOrderRequestForm';
import { OrderRequestForm } from '@/components/Templates/Requests';
import ReturnOrderForm from '@/components/Templates/Returns/ReturnOrderForm';
import { SupportTicketForm } from '@/components/Templates/SupportTickets/SupportTicketForm';
import {
	BgProcessModule,
	useCreateBgProcess,
} from '@/hooks/service-hooks/bgProcess';
import usePermission from '@/hooks/usePermission';
import { useDeleteDraft, useGetDrafts } from '@/hooks/service-hooks/drafts';
import { useAuthStore } from '@/store';
import type { Draft, DraftFormSuccessResult } from '@/types/draft';
import type { FilterType } from '@/types/table';
import { formatDate } from '@/utils';
import { draftTypeFilterOptions, statusFilterOptions } from './constants';

const DraftsTable = () => {
	const [brand600] = useToken('colors', ['brand.600']);
	const user = useAuthStore((state) => state.user?.userInformation);
	const userId = user?._id || '';
	const { hasPermission } = usePermission();

	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: !!userId,
		status: '',
		globalFilters: '[]',
		sort: '',
	});

	// Parse column filters - keep it simple, let backend handle the logic
	const columnFiltersArray = useMemo(() => {
		try {
			return JSON.parse(filters.columnsFilters || '[]');
		} catch (e) {
			console.error('Error parsing filters:', e);
			return [];
		}
	}, [filters.columnsFilters]);

	const { data, isFetching, refetch } = useGetDrafts({
		userId,
		page: filters.page,
		limit: filters.limit,
		search: filters.search,
		columnFilters: columnFiltersArray,
		enabled: filters.enabled,
	});

	// Refetch when filters change
	useEffect(() => {
		if (userId) {
			refetch();
		}
	}, [filters.columnsFilters, filters.page, filters.limit, filters.search, filters.sort, refetch, userId]);

	const { mutate: deleteDraft, isLoading: isDeleting } = useDeleteDraft();

	const [modalInformation, setModalInformation] = useState<{
		mode: 'deleteConfirm' | 'editDraft' | '';
		draftId?: string;
		draft?: Draft;
	}>({
		mode: '',
	});

	const [isFormDirty, setIsFormDirty] = useState(false);
	const [exitDraftAction, setExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);

	const closeEditDraftModal = () => {
		setModalInformation({ mode: '' });
		setIsFormDirty(false);
		refetch();
	};

	const removeDraftRow = ({
		draftId,
		showToast = false,
	}: {
		draftId: string;
		showToast?: boolean;
	}) => {
		deleteDraft(draftId, {
			onSuccess: () => {
				if (showToast) {
					toast.success('Draft deleted successfully');
				}
				setModalInformation({ mode: '' });
				setIsFormDirty(false);
				refetch();
			},
		});
	};

	const handleDraftFormCompletion = (result?: DraftFormSuccessResult) => {
		if (result?.action === 'draft_saved') {
			closeEditDraftModal();
			return;
		}

		if (modalInformation.draftId) {
			removeDraftRow({ draftId: modalInformation.draftId });
			return;
		}

		closeEditDraftModal();
	};

	const { mutate: createPatientFromDraft, isLoading: isSubmittingPatientDraft } =
		useCreateBgProcess(() => {
			handleDraftFormCompletion({ action: 'submitted' });
		});

	const handleSubmitDraftPatient = async (formData: PatientFormData) => {
		const payload = {
			firstname: formData.firstname,
			ta_emrid: formData.ta_emrid,
			lastname: formData.lastname,
			gendercode: formData.gendercode,
			ta_primarymemberid: formData.ta_primarymemberid,
			preferredcontactmethodcode: formData.preferredcontactmethodcode,
			mobilephone: formData.mobilephone,
			ta_secondarymemberid: formData.ta_secondarymemberid,
			emailaddress1: formData.emailaddress1,
			address1_line1: formData.address1_line1,
			address1_city: formData.address1_city,
			address1_stateorprovince: formData.address1_stateorprovince,
			address1_postalcode: formData.address1_postalcode,
			birthdate: dayjs(formData.birthdate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
			'ta_PrimaryInsurance@odata.bind': formData.primaryInsurance?.value
				? `ta_insurances(${formData.primaryInsurance?.value})`
				: undefined,
			'ta_SecondaryInsurance@odata.bind': formData.secondaryInsurance?.value
				? `ta_insurances(${formData.secondaryInsurance?.value})`
				: undefined,
			'parentcustomerid_account@odata.bind': formData.clinic?.value
				? `accounts(${formData.clinic?.value})`
				: undefined,
			ta_originationsource: '953870002',
			customertypecode: '100000011',
		};

		createPatientFromDraft({
			moduleName: BgProcessModule.PATIENTS,
			userId,
			payload: {
				payload,
				additionalPayload: {
					clinicName: formData.clinic?.label as string,
					fullName: `${formData.firstname} ${formData.lastname}`,
					primaryInsuranceName: formData.primaryInsurance?.label as string,
					secondaryInsuranceName: formData.secondaryInsurance?.label as string,
				},
			},
		});
	};

	const renderDraftForm = () => {
		if (!modalInformation.draft) {
			return null;
		}

		switch (modalInformation.draft.formType) {
			case 'BENEFIT_VERIFICATION':
				return (
					<BenefitVerificationForm
						existingDraft={modalInformation.draft}
						onSuccess={handleDraftFormCompletion}
						onIsDirtyChange={setIsFormDirty}
						onExitConfirmationDraftActionChange={setExitDraftAction}
					/>
				);
			case 'PATIENT':
				return (
					<PatientForm
						mode="create"
						existingDraft={modalInformation.draft}
						onSubmit={handleSubmitDraftPatient}
						isLoading={isSubmittingPatientDraft}
						onSuccess={handleDraftFormCompletion}
						onIsDirtyChange={setIsFormDirty}
						onExitConfirmationDraftActionChange={setExitDraftAction}
					/>
				);
			case 'RETURN_ORDER':
				return (
					<ReturnOrderForm
						existingDraft={modalInformation.draft}
						onSuccess={() => handleDraftFormCompletion({ action: 'submitted' })}
						onIsDirtyChange={setIsFormDirty}
						onExitConfirmationDraftActionChange={setExitDraftAction}
					/>
				);
			case 'CONSIGNMENT_ORDER':
				return (
					<BulkConsignmentOrderForm
						mode="create"
						existingDraft={modalInformation.draft}
						onSuccess={() => handleDraftFormCompletion({ action: 'submitted' })}
						onIsDirtyChange={setIsFormDirty}
						onExitConfirmationDraftActionChange={setExitDraftAction}
					/>
				);
			case 'SUPPORT_TICKET':
				return (
					<SupportTicketForm
						mode="create"
						existingDraft={modalInformation.draft}
						onSuccess={handleDraftFormCompletion}
						onIsDirtyChange={setIsFormDirty}
						onExitConfirmationDraftActionChange={setExitDraftAction}
					/>
				);
			case 'ORDER_REQUEST_PATIENT':
				return (
					<OrderRequestForm
						mode="create"
						existingDraft={modalInformation.draft}
						onSuccess={handleDraftFormCompletion}
						onIsDirtyChange={setIsFormDirty}
						onExitConfirmationDraftActionChange={setExitDraftAction}
					/>
				);
			case 'ORDER_REQUEST_CLINIC':
				return (
					<BulkOrderRequestForm
						mode="create"
						existingDraft={modalInformation.draft}
						onSuccess={handleDraftFormCompletion}
						onIsDirtyChange={setIsFormDirty}
						onExitConfirmationDraftActionChange={setExitDraftAction}
					/>
				);
			default:
				return null;
		}
	};

	// Handle edit draft - open modal directly instead of navigating
	const handleEditDraft = (id: string) => {
		const draft = data?.items.find((d: Draft) => d._id === id);
		if (!draft) return;

		setModalInformation({
			mode: 'editDraft',
			draftId: id,
			draft,
		});
	};

	// Handle delete draft
	const columns: Column[] = useMemo(
		() => [
			{
				key: 'createdAt',
				label: 'Create Date',
				width: '120px',
				type: 'date',
				dateISO: true,
				sorter: true,
			},
			{
				key: 'draftType',
				label: 'Draft Type',
				width: '180px',
				type: 'string',
				sorter: true,
				filterOptions: draftTypeFilterOptions,
			},
			{
				key: 'titleOrReference',
				label: 'Title / Reference',
				width: '200px',
				type: 'string',
				sorter: true,
			},
			{
				key: 'userName',
				label: 'Created By',
				width: '150px',
				type: 'string',
				sorter: true,
			},
			{
				key: 'status',
				label: 'Status',
				width: '120px',
				type: 'string-status',
				sorter: false,
				filterOptions: statusFilterOptions,
			},
		],
		[],
	);

	// Transform API data to table format
	const transformedData = useMemo(() => {
		if (!data?.items) return [];

		return data.items.map((item: Draft) => ({
			id: item._id,
			createdAt: formatDate(item.createdAt),
			draftType: item.draftType,
			titleOrReference: item.titleOrReference,
			userName: item.userName,
			status: (
				<Box
					bg="gray.500"
					borderRadius={4}
					color="white"
					height="24px"
					px={3}
					display="flex"
					alignItems="center"
					justifyContent="center"
					fontWeight={500}
					fontSize="sm"
				>
					Draft
				</Box>
			),
		}));
	}, [data]);

	// Handle filter apply
	const onFilterApply = ({
		page,
		limit,
		search = '',
		sort = null,
		columnsFilters = [],
		globalFilters = [],
	}: onFilterApplyProps) => {
		setFilters({
			...filters,
			page,
			limit,
			search,
			columnsFilters: JSON.stringify(columnsFilters),
			globalFilters: JSON.stringify(globalFilters),
			sort: sort ? `${sort.key}|${sort.direction}` : '',
		});
	};

	// Handle delete draft
	const handleDeleteDraft = (id: string) => {
		const draft = data?.items.find((d: Draft) => d._id === id);
		setModalInformation({
			mode: 'deleteConfirm',
			draftId: id,
			draft,
		});
	};

	// Mutations require Draft:Create; viewing the page/list uses Draft:Visualize (nav + API GET)
	const actions = [
		{
			label: 'Edit',
			icon: <MdEdit />,
			show: () => hasPermission(['Draft:Create']),
			actionFn: (item: ColumnData) => {
				handleEditDraft(item.id as string);
			},
		},
		{
			label: 'Delete',
			icon: <MdDeleteOutline />,
			show: () => hasPermission(['Draft:Create']),
			actionFn: (item: ColumnData) => {
				handleDeleteDraft(item.id as string);
			},
		},
	];

	return (
		<Box
			p={{ base: 4, md: 8 }}
			bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			position="relative"
		>
			<DataTable
				tableName="Drafts"
				moduleName="Drafts"
				loading={isFetching}
				items={transformedData}
				columns={columns}
				topLabel="Drafts"
				totalCount={data?.meta.totalRecords || 0}
				onFilterApply={onFilterApply}
				searchable
				hideShowColumn
				expandable
				paginated
				fullscreen
				bordered
				actions={actions}
			/>

			{/* Delete Confirmation Modal */}
			<FaceliftDialog
				open={modalInformation.mode === 'deleteConfirm'}
				onOpenChange={(openState) => {
					if (!openState) {
						setModalInformation({ mode: '' });
					}
				}}
				title="Delete Draft"
				showBackBtn={true}
				sx={{
					width: '500px',
					maxWidth: '95vw',
				}}
			>
				<Box p={4}>
					<Box mb={4}>
						Are you sure you want to delete this draft? This action cannot be
						undone.
					</Box>
					<Box display="flex" gap={3} justifyContent="flex-end">
						<FaceliftButton
							variant="outline"
							bg="white"
							color="gray.800"
							border="1px solid"
							onClick={() => setModalInformation({ mode: '' })}
						>
							Cancel
						</FaceliftButton>
						<FaceliftButton
							bg="red.500"
							_hover={{ bg: 'red.600' }}
							color="white"
							onClick={() => {
								if (modalInformation.draftId) {
									removeDraftRow({
										draftId: modalInformation.draftId,
										showToast: true,
									});
								}
							}}
							disabled={isDeleting}
							loading={isDeleting}
						>
							{isDeleting ? 'Deleting...' : 'Delete'}
						</FaceliftButton>
					</Box>
				</Box>
			</FaceliftDialog>

			{/* Edit Draft Modal - dynamically render form based on draft type */}
			<FaceliftDialogWithExitConfirmation
				open={modalInformation.mode === 'editDraft'}
				onOpenChange={(openState) => {
					if (!openState) {
						setModalInformation({ mode: '' });
						setIsFormDirty(false);
					}
				}}
				title={`Edit ${modalInformation.draft?.draftType || 'Draft'}`}
				showBackBtn={true}
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '840px',
					maxWidth: '95vw',
					maxHeight: '90vh',
					overflowY: 'auto',
				}}
				isDirty={isFormDirty}
				saveDraftAction={exitDraftAction}
			>
				{renderDraftForm()}
			</FaceliftDialogWithExitConfirmation>
		</Box>
	);
};

export default DraftsTable;
